import { Flex, Tooltip } from "antd";
import { FC, useMemo } from "react";
import { PointProductProgress, ProductPlan } from "tm-shared/types";

import { formatKPIValue, KPI_ICONS } from "@/domains/kpi";
import { PrimaryText } from "@/shared/ui";

import { StyledProductInfoText } from "../../GeneralProductCard.styled";
import {
    StyledKpiInfoIcon,
    StyledMainKpiValue,
    StyledTooltipLabel,
    StyledTooltipValue,
} from "./ProductKpiInfo.styled";

type KpiValues = Partial<Record<"b" | "l" | "m", number>>;
type KpiKey = keyof KpiValues;

const ICON_KEYS: KpiKey[] = ["b", "l", "m"];

type Props = {
    plan?: ProductPlan | null;
    fact?: PointProductProgress | null;
    additionalPlan?: ProductPlan | null;
    onTouchStart?: () => void;
};

export const ProductKpiInfo: FC<Props> = ({
    plan,
    fact,
    additionalPlan,
    onTouchStart,
}) => {
    const factKpi: KpiValues | null = useMemo(
        () =>
            fact ? { b: fact.bottle, l: fact.capacity, m: fact.money } : null,
        [fact],
    );

    const hasValues = (values?: KpiValues | ProductPlan | null) =>
        !!values && Object.values(values).some((v) => v && v !== 0);

    const renderRow = (label: string | null, values: KpiValues) => (
        <Flex align="center" justify="space-between" gap={10}>
            {label && <StyledTooltipLabel>{label}</StyledTooltipLabel>}

            <Flex gap={10}>
                {ICON_KEYS.map((key) => {
                    const Icon = KPI_ICONS[key];

                    return (
                        <Flex key={key} align="center" gap={4}>
                            <StyledTooltipValue>
                                {formatKPIValue({
                                    value: values[key],
                                    motivationType: key,
                                })}
                            </StyledTooltipValue>

                            {Icon && (
                                <PrimaryText fontSize={20}>
                                    <Icon />
                                </PrimaryText>
                            )}
                        </Flex>
                    );
                })}
            </Flex>
        </Flex>
    );

    const renderBlock = (
        title: string,
        planValues?: ProductPlan | null,
        factValues?: KpiValues | null,
        tooltipDescription?: string,
    ) => {
        if (!hasValues(planValues) && !hasValues(factValues)) return null;

        const planValue = planValues?.b ?? 0;
        const factValue = factValues?.b ?? 0;
        const hasPlan = planValue > 0;
        const hasFact = factValue > 0;
        const diff =
            hasPlan && hasFact ? Math.abs(planValue - factValue) : null;

        const tooltipContent = (
            <Flex vertical gap="8px" justify="center" align="center">
                {tooltipDescription && (
                    <StyledTooltipLabel>
                        {tooltipDescription}
                    </StyledTooltipLabel>
                )}
                {hasPlan && renderRow("План:", planValues!)}
                {hasFact && renderRow("Факт:", factValues!)}
            </Flex>
        );

        return (
            <StyledProductInfoText
                onTouchStart={onTouchStart}
                style={{ touchAction: "manipulation" }}
            >
                {hasPlan && (
                    <>
                        {title}{" "}
                        <StyledMainKpiValue>{planValue} пл.</StyledMainKpiValue>
                    </>
                )}
                {hasFact && (
                    <>
                        {hasPlan && " / "}Факт:{" "}
                        <StyledMainKpiValue>{factValue} пл.</StyledMainKpiValue>
                    </>
                )}
                {hasPlan && hasFact && planValue !== factValue && (
                    <>
                        {" "}
                        ({planValue > factValue ? "<" : ">"}
                        {diff})
                    </>
                )}{" "}
                <Tooltip placement="top" title={tooltipContent}>
                    <StyledKpiInfoIcon />
                </Tooltip>
            </StyledProductInfoText>
        );
    };

    const blocks = [
        { title: "План:", plan, fact: factKpi },
        {
            title: "Додатковий план:",
            plan: additionalPlan,
            fact: factKpi,
            tooltip: "План з видалених ТТ маршруту",
        },
        { title: "Факт:", plan: null, fact: factKpi },
    ];

    for (const block of blocks) {
        if (hasValues(block.plan) || hasValues(block.fact)) {
            return (
                <>
                    {renderBlock(
                        block.title,
                        block.plan,
                        block.fact,
                        block.tooltip,
                    )}
                </>
            );
        }
    }

    return null;
};
